import { getToken, setToken } from '@bluefalcon/utils-package';
import { toast } from 'react-toastify';
import { authService } from '../services';
import { BF_REFRESH, BF_SESSION_ID, BF_TOKEN } from '../constants/cookies.constants';

type FetchApiProviderType = RequestInit & {
    ignoreErrors?: boolean;
};

let refreshPromise: Promise<void> | null = null;

export async function FetchApiProvider(url: RequestInfo, params: FetchApiProviderType = {}): Promise<Response> {

    const makeRequest = async (isRetry: boolean = false, auth?: string): Promise<Response> => {
        const authToken = auth ?? getToken(BF_TOKEN);
        const refreshToken = getToken(BF_REFRESH);

        params = {
            ...params,
            headers: {
                ...params.headers,
                "Authorization": `Bearer ${authToken}`,
            },
        };

        try {
            const res = await fetch(url, params as RequestInit);
            const { status, ok: success } = res;

            // if success just return response
            if (success) {
                return res;
            }

            const rawBody = await res.clone().text();
            const data = rawBody.trim() ? await res.clone().json() : {};

            const isUnauthorized = status === 401;
            const isForbidden = status === 403;
            const isError = status >= 400;
            const isPrismaError = data.message?.toLowerCase().includes('prisma');
            const shouldRetry = isUnauthorized && refreshToken && !isRetry;

            if (shouldRetry) {
                if (!refreshPromise) {
                    refreshPromise = (async () => {
                        try {
                            const { token: newToken, refresh, sessionId } = await authService.refresh();
                            if (newToken && refresh) {
                                setToken(BF_TOKEN, newToken);
                                setToken(BF_REFRESH, refresh);
                                setToken(BF_SESSION_ID, String(sessionId));
                            } else {
                                throw new Error('Invalid refresh response');
                            }
                        } catch (e) {
                            console.log('failed to refresh');
                            await logout();
                            throw e;
                        } finally {
                            refreshPromise = null;
                        }
                    })();
                }

                try {
                    await refreshPromise;
                    const freshToken = getToken(BF_TOKEN);
                    return makeRequest(true, freshToken ?? undefined);
                } catch (e) {
                    throw e;
                }
            } else if (isUnauthorized) {
                await logout();
            } else if (isForbidden) {
                toast.error("You do not have sufficient permission.");
            } else if (isError && !params.ignoreErrors) {
                if (isPrismaError) {
                    toast.error('Something went wrong.');
                } else if (data.error?.message) {
                    toast.error(data.error.message);
                } else if (data.message) {
                    if (Array.isArray(data.message)) {
                        toast.error(data.message.join(" "));
                    } else {
                        toast.error(data.message);
                    }
                }
            }
            return res;
        } catch (error: any) {
            if (error.name !== 'AbortError') {
                throw { response: {}, error };
            }
            throw error;
        }
    };

    return await makeRequest();
}

const logout = async () => {
    try {
        await authService.logout();
    } catch (error) { } finally {
        window.location.replace('/logout');
    }
};
